import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Input, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { MailingCampaign } from '../../services/MailingService';
import { getCampaignStatusLabel, MAILING_CAMPAIGN_STATUS_OPTIONS } from './mailingLabels';

type CampaignFilterStatus = 'DRAFT' | 'SENDING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

const DEFAULT_PAGE_SIZE = 10;

export interface MailingCampaignsTabProps {
  campaigns: MailingCampaign[];
  loading: boolean;
  onOpenCampaignModal: () => void;
  onOpenLogDrawer: (campaign: MailingCampaign) => void;
  onOpenStartCampaignModal: (campaign: MailingCampaign) => void;
  onStopCampaign: (campaignId: string) => Promise<void>;
  onCancelCampaign: (campaignId: string) => Promise<void>;
  formatDateTime: (value?: string | null) => string;
  formatTimeRange: (start?: string | null, end?: string | null) => string;
  campaignStatusColor: (status?: string) => string;
}

const normalizeText = (value?: string | null) => (value || '').trim().toLocaleLowerCase('tr-TR');

const STATUS_OPTIONS = MAILING_CAMPAIGN_STATUS_OPTIONS as unknown as { label: string; value: CampaignFilterStatus }[];

const MailingCampaignsTab: React.FC<MailingCampaignsTabProps> = ({
  campaigns,
  loading,
  onOpenCampaignModal,
  onOpenLogDrawer,
  onOpenStartCampaignModal,
  onStopCampaign,
  onCancelCampaign,
  formatDateTime,
  formatTimeRange,
  campaignStatusColor,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<CampaignFilterStatus[]>([]);
  const [pendingStatuses, setPendingStatuses] = useState<CampaignFilterStatus[]>([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const debouncedApplySearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedApplySearch.cancel();
    };
  }, [debouncedApplySearch]);

  const filteredCampaigns = useMemo(() => {
    const normalizedSearch = normalizeText(search);

    return campaigns
      .filter((campaign) => {
        if (!selectedStatuses.length) return true;
        return selectedStatuses.includes((campaign.status || 'DRAFT') as CampaignFilterStatus);
      })
      .filter((campaign) => {
        if (!normalizedSearch) return true;
        const searchable = [campaign.name, campaign.template?.name, campaign.status, getCampaignStatusLabel(campaign.status)]
          .map((value) => normalizeText(value))
          .join(' ');
        return searchable.includes(normalizedSearch);
      });
  }, [campaigns, search, selectedStatuses]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedStatuses]);

  const openFilterDrawer = () => {
    setPendingStatuses(selectedStatuses);
    setFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedStatuses(pendingStatuses);
    setPage(1);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setPendingStatuses([]);
  };

  const total = filteredCampaigns.length;

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={
          <RowTitle title='Kampanya Listesi' total={total} />
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value || '';
                setSearchInput(value);
                if (value.length >= 2) {
                  debouncedApplySearch(value);
                } else if (value.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilterDrawer}>
              Filtrele
            </Button>
            <Button type='primary' onClick={onOpenCampaignModal}>
              Ekle
            </Button>
          </Space>
        }
      >
        <Table<MailingCampaign>
          rowKey='id'
          loading={loading}
          dataSource={filteredCampaigns}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || DEFAULT_PAGE_SIZE);
            },
          }}
          columns={[
            {
              title: 'Ad',
              dataIndex: 'name',
              key: 'name',
              render: (name: string, record) => (
                <Button type='link' style={{ padding: 0 }} onClick={() => onOpenLogDrawer(record)}>
                  {name}
                </Button>
              ),
            },
            {
              title: 'Şablon',
              dataIndex: ['template', 'name'],
              key: 'templateName',
              render: (_: unknown, record) => record.template?.name || '-',
            },
            { title: 'Gecikme (dk)', dataIndex: 'delayMinutes', key: 'delayMinutes' },
            {
              title: 'Başlangıç',
              dataIndex: 'scheduledStartAt',
              key: 'scheduledStartAt',
              render: (value?: string | null) => formatDateTime(value),
            },
            {
              title: 'Saat Aralığı',
              key: 'sendWindow',
              render: (_: unknown, record) => formatTimeRange(record.sendWindowStartTime, record.sendWindowEndTime),
            },
            {
              title: 'Planlanan Bitiş',
              dataIndex: 'plannedEndAt',
              key: 'plannedEndAt',
              render: (value?: string | null) => formatDateTime(value),
            },
            {
              title: 'Durum',
              dataIndex: 'status',
              key: 'status',
              render: (status: string) => <Tag color={campaignStatusColor(status)}>{getCampaignStatusLabel(status)}</Tag>,
            },
            {
              title: 'Kişi Sayısı',
              key: 'recipientCount',
              render: (_: unknown, record) => record.recipientCount ?? record.contactIds?.length ?? 0,
            },
            { title: 'Sıra', dataIndex: 'currentIndex', key: 'currentIndex' },
            {
              title: 'Son Gönderim',
              dataIndex: 'lastSentAt',
              key: 'lastSentAt',
              render: (value?: string | null) => formatDateTime(value),
            },
            {
              title: 'İşlem',
              key: 'actions',
              render: (_: unknown, record) => (
                <Space size={4} wrap>
                  {(record.status === 'DRAFT' || record.status === 'PAUSED') && (
                    <Button type='link' style={{ padding: 0 }} onClick={() => onOpenStartCampaignModal(record)}>
                      {record.status === 'PAUSED' ? 'Devam Et' : 'Başlat'}
                    </Button>
                  )}
                  {record.status === 'SENDING' && (
                    <Popconfirm title='Kampanya durdurulsun mu?' okText='Onayla' cancelText='Vazgeç' onConfirm={() => onStopCampaign(record.id)}>
                      <Button type='link' style={{ padding: 0 }}>
                        Durdur
                      </Button>
                    </Popconfirm>
                  )}
                  {(record.status === 'DRAFT' || record.status === 'PAUSED') && (
                    <Popconfirm title='Kampanya iptal edilsin mi?' okText='Onayla' cancelText='Vazgeç' onConfirm={() => onCancelCampaign(record.id)}>
                      <Button type='link' danger style={{ padding: 0 }}>
                        İptal
                      </Button>
                    </Popconfirm>
                  )}
                  <Button type='link' style={{ padding: 0 }} onClick={() => onOpenLogDrawer(record)}>
                    Detay
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Drawer open={filterOpen} onClose={() => setFilterOpen(false)} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              style={{ width: '100%', marginTop: 8 }}
              value={pendingStatuses}
              onChange={(values) => setPendingStatuses(values as CampaignFilterStatus[])}
              placeholder='Durum seçiniz'
              options={STATUS_OPTIONS}
            />
          </div>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type='primary' onClick={applyFilters}>
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
};

const RowTitle: React.FC<{ title: string; total: number }> = ({ title, total }) => {
  const { Text } = Typography;
  return (
    <Space align='center' size={12}>
      <Text style={{ fontWeight: 600 }}>{title}</Text>
      <Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
        <strong>({total})</strong> kayıt listelendi
      </Text>
    </Space>
  );
};

export default MailingCampaignsTab;
